import Backbone from 'backbone';

import * as analytics from 'analytics/Analytics';
import {AnalyticsEventOutcome} from 'analytics/types/AnalyticsEventOutcome';
import {assert} from 'Asserts';
import {ImportSourceType} from 'bulk-import/SpreadsheetBulkImportConstants';
import {
  ActionOrigin,
  MOLBIO_ANALYTICS_ACTIONS,
  REGISTRY_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {BioentityType, SequenceSubtype} from 'constants/ModelTypeConstants';
import {AutofillNames} from 'metadata/AutofillCompatibleBioentityOptions';
import {BIOENTITY_CREATION_LOCATIONS, OLIGO_CREATION_METHODS} from 'molbio/MolBioConstants';
import APIUtil from 'util/APIUtil';
import {Optional} from 'util/Types';

function trackEventIfNucleotideSequence(
  event: analytics.AnalyticsEvent,
  sequence: Optional<Backbone.Model>,
  metadata: {[key: string]: string | number | null} = {}
): void {
  if (sequence?.id && APIUtil.isNucleotideSequence(sequence.id)) {
    analytics.trackEvent(event, {sequence_id: sequence.id, ...metadata});
  }
}

export enum BaseSelectionMethod {
  clickAndDrag = 'click_and_drag',
  shiftClickDown = 'shift_click_down',
  shiftClickUp = 'shift_click_up',
  doubleClick = 'double_click',
}

const SEQUENCE_MAP_TO_TAB_NAME: {[key: string]: string} = {
  minimap: 'Linear Map',
  linear: 'Sequence Map',
};

export function trackSequenceMapBaseSelection(
  sequence: Backbone.Model,
  selectionMethod: BaseSelectionMethod,
  start?: number,
  end?: number | null,
  mapType?: string
): void {
  // make a best effort at converting the map type to a known tab name.
  if (end == null) {
    return;
  }
  const mapName = mapType ? (SEQUENCE_MAP_TO_TAB_NAME[mapType] ?? mapType) : null;
  trackEventIfNucleotideSequence(MOLBIO_ANALYTICS_ACTIONS.SELECTED_BASES_IN_MAP, sequence, {
    start_index: start ?? null,
    end_index: end ?? null,
    click_selection_method: selectionMethod,
    maybe_map_name: mapName ?? null,
  });
}

export function trackSequenceMapTabView(sequence: Backbone.Model, tabTitle: string | undefined): void {
  trackEventIfNucleotideSequence(MOLBIO_ANALYTICS_ACTIONS.VIEWED_SEQUENCE_ENTITY_MAP_TAB, sequence, {
    tab_title: tabTitle ?? null,
  });
}

export function trackMetadataTabViewIfValidSequence(sequence: Backbone.Model): void {
  trackEventIfNucleotideSequence(MOLBIO_ANALYTICS_ACTIONS.VIEWED_SEQUENCE_ENTITY_METADATA_TAB, sequence);
}

export function trackDescriptionTabViewIfValidSequence(sequence: Backbone.Model): void {
  trackEventIfNucleotideSequence(MOLBIO_ANALYTICS_ACTIONS.VIEWED_SEQUENCE_ENTITY_DESCRIPTION_TAB, sequence);
}

export function trackSequenceMapClick(sequence: Backbone.Model, mapType: string | undefined): void {
  const mapName = mapType ? (SEQUENCE_MAP_TO_TAB_NAME[mapType] ?? mapType) : null;
  trackEventIfNucleotideSequence(MOLBIO_ANALYTICS_ACTIONS.CLICKED_SEQUENCE_ENTITY_SEQUENCE_MAP, sequence, {
    maybe_map_name: mapName ?? null,
  });
}

export function trackSequenceMapClickCreate(sequence: Backbone.Model): void {
  trackEventIfNucleotideSequence(MOLBIO_ANALYTICS_ACTIONS.CLICKED_CREATE_IN_SEQUENCE_MAP, sequence);
}

export function trackSequenceMapClickAnalyze(sequence: Backbone.Model): void {
  trackEventIfNucleotideSequence(MOLBIO_ANALYTICS_ACTIONS.CLICKED_ANALYZE_IN_SEQUENCE_MAP, sequence);
}

export function trackSequenceMapRightClickWithSelectedBases(sequence: Backbone.Model): void {
  trackEventIfNucleotideSequence(MOLBIO_ANALYTICS_ACTIONS.CLICKED_RIGHT_ON_SELECTED_SEQUENCE_BASES, sequence);
}

export function trackSequenceMapCtrlF(sequence: Backbone.Model | null): void {
  trackEventIfNucleotideSequence(MOLBIO_ANALYTICS_ACTIONS.OPENED_SEQUENCE_MAP_SEARCH_MODAL, sequence);
}

export function trackOpenedRNAFromSequenceMap(): void {
  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.OPENED_RNA_SEQUENCE_FROM_TRANSCRIPTION_VISUALIZATION);
}

export function trackSubmitAutoFillFeature(
  fileIds: Array<string>,
  actionOrigin: ActionOrigin,
  autofillName: AutofillNames
): void {
  const sequenceIds = fileIds.filter((fileId) => APIUtil.isNucleotideSequence(fileId));
  assert(
    sequenceIds.length === fileIds.length,
    `Autofill-${autofillName} submitted in ${actionOrigin} with non nucleotide sequences`
  );
  analytics.trackEvent(REGISTRY_ANALYTICS_ACTIONS.SUBMITTED_AUTOFILL_SEQUENCE_FEATURE, {
    // Segment creates a separate column for each entry in an array. Convert the array to a string here
    // to avoid that behavior.
    sequence_ids: sequenceIds.toString(),
    action_origin: actionOrigin,
    autofill_feature: autofillName,
  });
}

export function trackClickOnAnnotationInSequence(
  sequence: Backbone.Model,
  start?: number,
  end?: number
): void {
  trackEventIfNucleotideSequence(MOLBIO_ANALYTICS_ACTIONS.CLICKED_ANNOTATION_IN_SEQUENCE_MAP, sequence, {
    start_index: start ?? null,
    end_index: end ?? null,
  });
}

export function trackClickOnCreateNewOligo(
  actionOrigin: BIOENTITY_CREATION_LOCATIONS,
  initialImportFlow: OLIGO_CREATION_METHODS
): void {
  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.OPENED_CREATE_OLIGO_MODAL, {
    // @ts-expect-error: TODO: Should be using ActionOrigin type
    action_origin: actionOrigin,
    initial_import_flow: initialImportFlow,
  });
}

export function trackEnterValueInNucleotideCreateModal(itemType: string, fieldName: string): void {
  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.ENTERED_VALUE_IN_NUCLEOTIDE_CREATE_MODAL, {
    item_type: itemType,
    field_name: fieldName,
  });
}

export function trackNucleotideCreateModalSubmit(itemType: string): void {
  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.SUBMITTED_NUCLEOTIDE_CREATE_MODAL, {
    item_type: itemType,
  });
}

export function trackUploadSpreadsheetInNucleotideImportModal(
  itemType: SequenceSubtype,
  uploadType: ImportSourceType,
  outcome: AnalyticsEventOutcome,
  errorMessage?: string
): void {
  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPLOADED_NUCLEOTIDE_IMPORT_SPREADSHEET, {
    item_type: itemType,
    upload_type: uploadType,
    outcome,
    error_message: errorMessage,
  });
}

export function trackSubmitInBioentitySpreadsheetImportModal(entityType: BioentityType): void {
  analytics.trackEvent(REGISTRY_ANALYTICS_ACTIONS.SUBMITTED_BIOENTITY_SPREADSHEET_IMPORT_MODAL, {
    item_type: entityType,
  });
}
